import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { Warehouse } from '../../../types/bookkeeping.js';

export function WarehousesPage() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();

  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['bk-warehouses', activeOrgId],
    queryFn: () => api<{ warehouses: Warehouse[] }>('/bookkeeping/inventory/warehouses'),
    enabled: !!activeOrgId,
  });

  const warehouses = data?.warehouses ?? [];

  const saveMutation = useMutation({
    mutationFn: () => {
      const url = editing
        ? `/bookkeeping/inventory/warehouses/${editing.id}`
        : '/bookkeeping/inventory/warehouses';
      const method = editing ? 'PATCH' : 'POST';
      return api<{ warehouse: Warehouse }>(url, {
        method,
        body: JSON.stringify({ code, name, is_default: isDefault }),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bk-warehouses'] });
      setCreating(false);
      setEditing(null);
      setCode('');
      setName('');
      setIsDefault(false);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message ?? 'unknown_error');
    },
  });

  function openCreate() {
    setCreating(true);
    setEditing(null);
    setCode('');
    setName('');
    setIsDefault(false);
    setError(null);
  }

  function openEdit(w: Warehouse) {
    setEditing(w);
    setCreating(false);
    setCode(w.code);
    setName(w.name);
    setIsDefault(w.is_default);
    setError(null);
  }

  function closeForm() {
    setCreating(false);
    setEditing(null);
    setError(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('inventory.warehouses.title')}</h1>
        <button
          type="button"
          onClick={openCreate}
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('inventory.warehouses.new')}
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('inventory.warehouses.code')}</th>
                <th className="px-3 py-2">{t('inventory.warehouses.name')}</th>
                <th className="px-3 py-2">{t('inventory.warehouses.is_default')}</th>
                <th className="px-3 py-2">{t('inventory.warehouses.is_active')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {warehouses.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                    {t('inventory.warehouses.empty')}
                  </td>
                </tr>
              )}
              {warehouses.map((w) => (
                <tr key={w.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono">{w.code}</td>
                  <td className="px-3 py-2">{w.name}</td>
                  <td className="px-3 py-2">{w.is_default ? '✓' : '—'}</td>
                  <td className="px-3 py-2">{w.is_active ? '✓' : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(w)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {t('common.edit')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <div className="rounded border bg-slate-50 p-4">
          <h2 className="mb-3 text-base font-medium">
            {editing ? t('inventory.warehouses.editTitle') : t('inventory.warehouses.new')}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm">{t('inventory.warehouses.code')}</span>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="MAIN"
                className="mt-1 w-full rounded border px-2 py-1 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm">{t('inventory.warehouses.name')}</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('inventory.warehouses.name')}
                className="mt-1 w-full rounded border px-2 py-1 text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              {t('inventory.warehouses.is_default')}
            </label>
          </div>
          {error && (
            <div className="mt-2 text-sm text-red-600">
              {t(`inventory.errors.${error}`, { defaultValue: error })}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              {t('common.save')}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="rounded border px-3 py-1.5 text-sm"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
